/**
 * SettingsPage — tabbed settings hub.
 *
 * Tabs:
 *   General    — display name, email (read-only), timezone
 *   LinkedIn   — LinkedInConnect component
 *   ICP        — IcpSettings component
 *   Calendar   — CalendarSettings (AI-booked meetings)
 *   Claude     — ClaudeSettings (Use DCA in Claude: MCP connections)
 *   Account    — plan info, billing, danger zone
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/components/layout/page-header'
import { WizardReturnBanner } from '@/features/campaigns/wizard-return-banner'
import type { CheckoutResponse } from '@/components/plan-gate'
import { SectionHelp } from '@/components/help/section-help'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { ExternalLink, Loader2, CheckCircle2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useUser, useClerk } from '@clerk/clerk-react'
import { usePlan } from '@/hooks/use-plan'
import LinkedInConnect from '@/features/settings/linkedin-connect'
import IcpSettings from '@/features/settings/icp-settings'
import OfferSettings from '@/features/settings/offer-settings'
import MessageSettings from '@/features/settings/message-settings'
import ContentSettings from '@/features/settings/content-settings'
import FaqSettings from '@/features/settings/faq-settings'
import ObjectionSettings from '@/features/settings/objection-settings'
import VoiceSettings from '@/features/settings/voice-settings'
import SafetySettings from '@/features/settings/safety-settings'
import CalendarSettings from '@/features/settings/calendar-settings'
import IntegrationsSettings from '@/features/settings/integrations-settings'
import ClaudeSettings from '@/features/settings/claude-settings'
import { useApi } from '@/hooks/use-api'
import { SUPPORT_EMAIL } from '@dealcloser/shared'

// ============================================================
// Static data
// ============================================================

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
] as const

// ============================================================
// General tab
// ============================================================

function GeneralTab() {
  const { user } = useUser()
  const apiFetch = useApi()
  const [displayName, setDisplayName] = useState(
    () => user?.fullName ?? [user?.firstName, user?.lastName].filter(Boolean).join(' ') ?? ''
  )
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  )
  const [saving, setSaving] = useState(false)
  const [savedSuccess, setSavedSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Load saved timezone from backend on mount
  const { data: savedPrefs } = useQuery({
    queryKey: ['settings', 'general'],
    queryFn: async () => {
      const res = await apiFetch<{ timezone?: string }>('/api/settings/general')
      return res.data ?? null
    },
    staleTime: 5 * 60_000,
  })

  // Apply saved timezone once loaded
  useState(() => {
    if (savedPrefs?.timezone) setTimezone(savedPrefs.timezone)
  })

  // Update timezone when backend data arrives
  useEffect(() => {
    if (savedPrefs?.timezone) setTimezone(savedPrefs.timezone)
  }, [savedPrefs])

  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    const res = await apiFetch('/api/settings/general', {
      method: 'PUT',
      body: { timezone, displayName: displayName.trim() },
    })
    setSaving(false)
    if (!res.success) {
      setSaveError(res.error ?? 'Failed to save preferences')
      return
    }
    setSavedSuccess(true)
    setTimeout(() => setSavedSuccess(false), 3000)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Your public display name and account email.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="display-name">Display Name</Label>
            <Input
              id="display-name"
              placeholder="Your name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={user?.primaryEmailAddress?.emailAddress ?? ''}
              readOnly
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Managed by your authentication provider. Contact support to change.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
          <CardDescription>Timezone used for scheduling and activity reports.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger id="timezone" className="w-full max-w-xs">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="default" size="sm" onClick={() => void handleSave()} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </Button>
            {savedSuccess && (
              <span className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Saved
              </span>
            )}
            {saveError && <span className="text-sm text-destructive">{saveError}</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ============================================================
// Account tab
// ============================================================

/**
 * Live cancel-flow state from GET /api/billing/cancel-flow (webhooks worker,
 * read straight from Stripe). has_subscription:false covers comp accounts,
 * free users, and rows whose subscription link is gone — for all of those the
 * cancel controls simply don't render.
 */
type CancelFlowState = {
  readonly has_subscription: boolean
  readonly status?: string
  readonly cancel_at_period_end?: boolean
  readonly current_period_end?: string | null
  readonly retention_offer_used?: boolean
  readonly reason?: string
}

/** "January 5, 2027" — used for renewal / cancellation dates. */
function formatBillingDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function AccountTab() {
  const apiFetch = useApi()
  const { user } = useUser()
  const { signOut } = useClerk()
  const effectivePlan = usePlan()
  const [portalLoading, setPortalLoading] = useState(false)
  const [portalError, setPortalError] = useState<string | null>(null)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [checkoutUpgraded, setCheckoutUpgraded] = useState<string | null>(null)
  const queryClient = useQueryClient()
  // Account deletion state
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // In-app cancel flow (replaced the old "cancel via email" mailto,
  // 2026-09-01 owner directive): dialog with a one-time 30% retention offer,
  // then a confirm step that schedules cancellation at period end.
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelStep, setCancelStep] = useState<'retention' | 'confirm' | 'offer_applied'>('retention')
  const [cancelBusy, setCancelBusy] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [offerPriceCents, setOfferPriceCents] = useState<number | null>(null)
  const [resumeBusy, setResumeBusy] = useState(false)
  const [cancelNotice, setCancelNotice] = useState<string | null>(null)

  // Prefer the effective plan (D1 + comp override) over raw Clerk metadata.
  // Clerk publicMetadata frequently lags behind D1 (Stripe webhook updates D1
  // first; Clerk sync isn't guaranteed). usePlan() handles that fallback chain.
  const currentPlan = effectivePlan
  const planLabel = currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)
  const hasSubscription = currentPlan !== 'starter'
  const userEmail = user?.primaryEmailAddress?.emailAddress ?? ''

  // Live subscription state straight from Stripe (renewal date, scheduled
  // cancellation, whether the retention offer was already used). null while
  // loading or on error — the cancel controls just don't render then, and
  // Manage Billing / support remain available.
  const { data: cancelFlow } = useQuery({
    queryKey: ['billing-cancel-flow'],
    queryFn: async () => {
      const res = await apiFetch<CancelFlowState>('/api/billing/cancel-flow')
      return res.success ? (res.data ?? null) : null
    },
    staleTime: 30_000,
  })

  const scheduledCancel = cancelFlow?.has_subscription === true && cancelFlow.cancel_at_period_end === true
  const canCancel = cancelFlow?.has_subscription === true && cancelFlow.cancel_at_period_end !== true
  const periodEndLabel = cancelFlow?.current_period_end
    ? formatBillingDate(cancelFlow.current_period_end)
    : null

  function openCancelDialog() {
    setCancelError(null)
    setOfferPriceCents(null)
    // The retention offer is once per subscription — when it was already
    // used, go straight to the confirm step instead of dangling a discount
    // the backend will refuse.
    setCancelStep(cancelFlow?.retention_offer_used ? 'confirm' : 'retention')
    setCancelOpen(true)
  }

  async function postCancelFlow(action: 'retention_accept' | 'cancel' | 'resume') {
    return apiFetch<{
      discounted_amount_cents?: number | null
      cancels_at?: string | null
      cancel_at_period_end?: boolean
    }>('/api/billing/cancel-flow', { method: 'POST', body: { action } })
  }

  async function handleAcceptOffer() {
    setCancelBusy(true)
    setCancelError(null)
    const res = await postCancelFlow('retention_accept')
    setCancelBusy(false)
    if (!res.success) {
      const code = res.error ?? ''
      setCancelError(
        code.includes('retention_already_used')
          ? 'This offer was already used on your subscription, so it cannot be applied again.'
          : `We could not apply the discount. Please try again, or email ${SUPPORT_EMAIL}.`,
      )
      return
    }
    setOfferPriceCents(res.data?.discounted_amount_cents ?? null)
    setCancelStep('offer_applied')
    await queryClient.invalidateQueries({ queryKey: ['billing-cancel-flow'] })
  }

  async function handleConfirmCancel() {
    setCancelBusy(true)
    setCancelError(null)
    const res = await postCancelFlow('cancel')
    setCancelBusy(false)
    if (!res.success) {
      setCancelError(`We could not schedule the cancellation. Please try again, or email ${SUPPORT_EMAIL}.`)
      return
    }
    const when = res.data?.cancels_at ? formatBillingDate(res.data.cancels_at) : 'the end of your billing period'
    setCancelNotice(`Your subscription is set to cancel on ${when}. You keep full access until then.`)
    setCancelOpen(false)
    await queryClient.invalidateQueries({ queryKey: ['billing-cancel-flow'] })
  }

  async function handleResume() {
    setResumeBusy(true)
    setCancelError(null)
    const res = await postCancelFlow('resume')
    setResumeBusy(false)
    if (!res.success) {
      setCancelError(`We could not resume the subscription. Please try again, or email ${SUPPORT_EMAIL}.`)
      return
    }
    setCancelNotice('Welcome back. Your subscription will renew as usual.')
    await queryClient.invalidateQueries({ queryKey: ['billing-cancel-flow'] })
  }

  async function handleManageBilling() {
    setPortalLoading(true)
    setPortalError(null)
    const res = await apiFetch<{ url: string }>('/api/billing/portal', { method: 'POST' })
    setPortalLoading(false)
    if (!res.success || !res.data) {
      // /billing/portal now returns structured error codes in res.error:
      //   comp_account        — is_comp=1, no Stripe customer to manage
      //   no_stripe_customer  — paid plan but stripe_customer_id is NULL on D1
      //                         (v2-legacy migration, webhook never linked back)
      //   stripe_api_error    — Stripe rejected the portal session create
      //   user_not_found      — Clerk JWT valid but no D1 user row yet
      // Anything else (network, raw status fallback) → show the raw error.
      const code = res.error?.toLowerCase() ?? ''
      let friendly: string
      if (code.includes('comp_account')) {
        friendly =
          `Your account is complimentary — no Stripe billing to manage. Contact ${SUPPORT_EMAIL} if this is incorrect.`
      } else if (code.includes('no_stripe_customer') || code.includes('stripe customer')) {
        friendly =
          `No active subscription is linked here. Email ${SUPPORT_EMAIL} if you need help and we will handle it manually.`
      } else if (code.includes('stripe_api_error')) {
        friendly = `Stripe rejected the request. Email ${SUPPORT_EMAIL} for immediate help.`
      } else if (code.includes('user_not_found')) {
        friendly = `Your account is still syncing. Refresh in a moment, or email ${SUPPORT_EMAIL}.`
      } else {
        friendly = res.error ?? 'Failed to open billing portal'
      }
      setPortalError(friendly)
      return
    }
    window.open(res.data.url, '_blank')
  }

  async function handleDeleteAccount() {
    setDeleteLoading(true)
    setDeleteError(null)
    // Cancel the Stripe subscription FIRST so billing stops. DELETE /auth/me
    // wipes stripe_subscription_id in the D1 cascade, so we'd lose the link to
    // cancel afterwards. If cancellation fails, we abort the delete rather than
    // leave the customer billed for a wiped account.
    const cancelRes = await apiFetch<{ cancelled: boolean }>('/api/billing/cancel', { method: 'POST' })
    if (!cancelRes.success) {
      setDeleteLoading(false)
      setDeleteError(
        `We couldn't cancel your subscription, so we didn't delete the account (to avoid leaving you billed). Please try again, or contact ${SUPPORT_EMAIL}.`,
      )
      return
    }
    const res = await apiFetch<{ message: string; partial_errors?: Array<{ stage: string; message: string }> }>(
      '/api/auth/me',
      { method: 'DELETE' },
    )
    setDeleteLoading(false)
    if (!res.success) {
      const code = res.error?.toLowerCase() ?? ''
      if (code.includes('d1_cascade_failed')) {
        setDeleteError(
          `We couldn't fully delete your account. Contact ${SUPPORT_EMAIL} and we'll finish the job.`,
        )
      } else {
        setDeleteError(res.error ?? 'Failed to delete account')
      }
      return
    }
    // Sign out so they can't keep using a stale Clerk session, then send
    // them to the marketing site. The Clerk DELETE on the server will
    // also invalidate the session — this is just immediate-feedback UX.
    await signOut({ redirectUrl: '/' })
  }

  async function handleSubscribe() {
    setCheckoutLoading(true)
    setCheckoutError(null)
    const res = await apiFetch<CheckoutResponse>('/api/billing/checkout', { method: 'POST', body: { plan: 'pro' } })
    setCheckoutLoading(false)
    if (!res.success || !res.data) {
      setCheckoutError(res.error ?? 'Failed to start checkout')
      return
    }
    if (res.data.upgraded) {
      // Existing subscriber: the backend changed the plan in place (no
      // second subscription). Refresh the cached plan + reload the page.
      setCheckoutUpgraded('Upgraded to Pro! Refreshing…')
      await queryClient.invalidateQueries({ queryKey: ['user-me'] })
      setTimeout(() => window.location.reload(), 1200)
      return
    }
    if (!res.data.url) {
      setCheckoutError('Failed to start checkout')
      return
    }
    window.location.href = res.data.url
  }

  return (
    <div className="space-y-6">
      {/* Plan info */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Current Plan</CardTitle>
            <Badge className="bg-primary/10 text-cobalt-bright border-0">{planLabel}</Badge>
          </div>
          <CardDescription>Your subscription details and billing information.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Billing cycle</span>
            <span>Monthly</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{scheduledCancel ? 'Cancels on' : 'Next renewal'}</span>
            {scheduledCancel ? (
              <span className="font-medium text-amber-600 dark:text-amber-400">
                {periodEndLabel ?? 'End of billing period'}
              </span>
            ) : (
              <span>{periodEndLabel ?? '—'}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {/* Manage Billing — opens Stripe Customer Portal. Only meaningful
                for users whose checkout flow successfully linked a
                stripe_customer_id to their D1 row. */}
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => void handleManageBilling()}
              disabled={portalLoading}
            >
              {portalLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4" />
              )}
              Manage Billing
            </Button>
            {/* Upgrade — only when on the free/starter tier. */}
            {!hasSubscription && (
              <Button
                variant="default"
                className="gap-2"
                onClick={() => void handleSubscribe()}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Upgrade Plan
              </Button>
            )}
            {/* Resume — a scheduled cancellation can be undone any time
                before period end. */}
            {scheduledCancel && (
              <Button
                variant="default"
                size="sm"
                onClick={() => void handleResume()}
                disabled={resumeBusy}
              >
                {resumeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Resume subscription
              </Button>
            )}
            {/* Cancel — in-app (2026-09-01, replaced the old mailto). Quiet
                by design; only rendered for a real live subscription. */}
            {canCancel && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={openCancelDialog}
              >
                Cancel subscription
              </Button>
            )}
          </div>
          {portalError && <p className="text-sm text-destructive">{portalError}</p>}
          {checkoutError && <p className="text-sm text-destructive">{checkoutError}</p>}
          {cancelError && !cancelOpen && <p className="text-sm text-destructive">{cancelError}</p>}
          {checkoutUpgraded && (
            <p className="flex items-center gap-1.5 text-sm text-emerald-500">
              <CheckCircle2 className="h-4 w-4" /> {checkoutUpgraded}
            </p>
          )}
          {cancelNotice && (
            <p className="flex items-center gap-1.5 text-sm text-emerald-500">
              <CheckCircle2 className="h-4 w-4" /> {cancelNotice}
            </p>
          )}
          {scheduledCancel && (
            <p className="text-xs text-muted-foreground pt-1">
              Your plan stays fully active until {periodEndLabel ?? 'the end of your billing period'}. Changed your mind? Click "Resume subscription".
            </p>
          )}
        </CardContent>
      </Card>

      {/* Cancel-subscription dialog: retention offer first (once per
          subscription), then the confirm step. Cancelling schedules the end
          of the subscription at period end via Stripe; the webhooks stay the
          source of truth for plan status and campaign pausing. */}
      <Dialog
        open={cancelOpen}
        onOpenChange={(open) => {
          if (!cancelBusy) setCancelOpen(open)
        }}
      >
        <DialogContent>
          {cancelStep === 'retention' && (
            <>
              <DialogHeader>
                <DialogTitle>Before you go, want 30% off instead?</DialogTitle>
                <DialogDescription>
                  Stay on your current plan and your next 3 invoices are 30% off. No other
                  changes, and you can still cancel later.
                </DialogDescription>
              </DialogHeader>
              {cancelError && <p className="text-sm text-destructive">{cancelError}</p>}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCancelError(null)
                    setCancelStep('confirm')
                  }}
                  disabled={cancelBusy}
                >
                  No thanks, continue cancelling
                </Button>
                <Button onClick={() => void handleAcceptOffer()} disabled={cancelBusy}>
                  {cancelBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Take 30% off
                </Button>
              </DialogFooter>
            </>
          )}

          {cancelStep === 'offer_applied' && (
            <>
              <DialogHeader>
                <DialogTitle>Discount applied</DialogTitle>
                <DialogDescription>
                  Your next 3 invoices are 30% off
                  {offerPriceCents != null
                    ? `, so you pay $${(offerPriceCents / 100).toFixed(2)}/month for the next 3 months.`
                    : '.'}{' '}
                  Thanks for staying.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => setCancelOpen(false)}>Done</Button>
              </DialogFooter>
            </>
          )}

          {cancelStep === 'confirm' && (
            <>
              <DialogHeader>
                <DialogTitle>Cancel your subscription?</DialogTitle>
                <DialogDescription>
                  Your subscription stays active until{' '}
                  {periodEndLabel ?? 'the end of your current billing period'}, then won't renew.
                  Your campaigns pause at that point. You can resume any time before then.
                </DialogDescription>
              </DialogHeader>
              {cancelError && <p className="text-sm text-destructive">{cancelError}</p>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancelBusy}>
                  Keep my plan
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void handleConfirmCancel()}
                  disabled={cancelBusy}
                >
                  {cancelBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Cancel subscription
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Danger zone */}
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>Irreversible actions. Proceed with caution.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border border-destructive/30 p-4">
            <div>
              <p className="text-sm font-medium">Delete Account</p>
              <p className="text-xs text-muted-foreground">
                Permanently delete your account and all associated data.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setDeleteOpen(true)
                setDeleteConfirm('')
                setDeleteError(null)
              }}
            >
              Delete Account
            </Button>
          </div>
          {deleteError && (
            <p className="text-xs text-destructive mt-2">{deleteError}</p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Trouble deleting? Email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a>{' '}
            and we'll handle it manually.
          </p>
        </CardContent>
      </Card>

      {/* Delete-account confirmation dialog — requires typing the exact
          email to enable the destructive button, matches the GitHub /
          Stripe pattern. */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete account?</DialogTitle>
            <DialogDescription>
              This permanently removes your DealCloser Autopilot account and
              every campaign, lead, and message tied to it. Your connected
              LinkedIn account is disconnected automatically. There is no
              undo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm">
              Type your email <span className="font-mono">{userEmail}</span> to confirm.
            </p>
            <input
              type="email"
              autoComplete="off"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder={userEmail}
            />
            {deleteError && (
              <p className="text-xs text-destructive">{deleteError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteLoading || deleteConfirm.trim().toLowerCase() !== userEmail.trim().toLowerCase()}
              onClick={() => void handleDeleteAccount()}
            >
              {deleteLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Delete my account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ============================================================
// Page
// ============================================================

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'general'

  function handleTabChange(value: string) {
    setSearchParams({ tab: value }, { replace: true })
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Settings"
        subtitle="Configure your account and automation preferences."
        actions={<SectionHelp sectionKey="settings" />}
      />

      <WizardReturnBanner message="You’re changing settings for a campaign you’re still setting up." />

      <Tabs defaultValue="general" value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-6 flex-wrap" data-tour="settings-tabs">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
          <TabsTrigger value="icp">ICP Targeting</TabsTrigger>
          <TabsTrigger value="outreach">Offer</TabsTrigger>
          <TabsTrigger value="voice-content">Voice & Content</TabsTrigger>
          <TabsTrigger value="safety">Safety</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="claude">Claude</TabsTrigger>
          <TabsTrigger value="account">Account</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <GeneralTab />
        </TabsContent>

        <TabsContent value="linkedin">
          <LinkedInConnect />
        </TabsContent>

        <TabsContent value="icp">
          <IcpSettings />
        </TabsContent>

        <TabsContent value="outreach">
          <div className="space-y-8">
            <OfferSettings />
            <MessageSettings />
            {/* The call to action + its link are campaign-level (owner
                directive 2026-08-30): set in the create wizard / Edit
                Campaign, not here. */}
            <FaqSettings />
            <ObjectionSettings />
          </div>
        </TabsContent>

        <TabsContent value="voice-content">
          <div className="space-y-8">
            <VoiceSettings />
            <ContentSettings />
          </div>
        </TabsContent>

        <TabsContent value="safety">
          <SafetySettings />
        </TabsContent>

        <TabsContent value="integrations">
          <IntegrationsSettings />
        </TabsContent>

        <TabsContent value="calendar">
          <CalendarSettings />
        </TabsContent>

        <TabsContent value="claude">
          <ClaudeSettings />
        </TabsContent>

        <TabsContent value="account">
          <AccountTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
